/**
 * Kamus namespace `kasir`.
 *
 * Tiering (`design/I18N.md` §2):
 * - **T1 tetap Indonesia di kedua mode:** PB1, PPN, QRIS, EDC — nama pajak/standar nasional.
 * - **T2 diterjemahkan:** Pesanan↔Order, Meja↔Table, Tutup Kasir↔Cash Close.
 * - **T3 tetap Inggris di kedua mode:** Takeaway, Dine-in, KDS, Void.
 */
export const cashierId = {
  clearCache: {
    button: 'Bersihkan salinan tablet',
    /* ⚠️ Menyebut ISInya, bukan kata "cache" — yang membacanya manajer outlet, bukan pengembang. */
    what: 'Membuang salinan menu, sesi kas, dan tanggal usaha yang tersimpan di tablet ini. Semuanya diambil ulang dari server saat layar dimuat.',
    keeps: 'TIDAK dibuang: pasangan perangkat (tablet tak perlu di-pair ulang) dan antrean penjualan.',
    pending: '{{n}} penjualan masih menunggu diakui portal. Kirim dulu — membersihkan sekarang menghapus uang yang sudah diterima dari tamu.',
    offline: 'Server tak terjangkau. Membersihkan sekarang membuang satu-satunya salinan menu tepat ketika ia tak bisa diambil lagi.',
    staffCode: 'Kode staf administrator',
    pin: 'PIN',
    needsPermission: 'Staf ini tak punya izin mengubah setelan (`master:edit`).',
    confirm: 'Bersihkan',
  },
  find: {
    title: 'Cari bill',
    /* ⭐ Menyebut yang kasir INGAT, bukan nama kolom. */
    hint: 'Meja, antrean, nama, atau nomor bill',
    search: 'Cari',
    empty: 'Tak ada bill yang cocok.',
    truncated: 'Hasil dipotong. Persempit pencarianmu.',
    close: 'Tutup',
    button: 'Cari bill',
    range: {
      0: 'Hari ini',
      1: 'Kemarin',
      7: '7 hari',
    },
  },
  printerSetup: {
    copy: 'Salin MAC',
    copied: '✓ tersalin',
    title: 'Pilih printer',
    use: 'Pakai',
    done: '{{name}} dipakai untuk terminal ini.',
    empty: 'Tak ada printer terlihat. Pastikan agen cetak terpasang & jalan, dan printernya sudah di-pair di Setelan → Bluetooth.',
    lanHint: 'Printer jaringan (LAN) & USB disetel di portal: Lantai → kolom Printer.',
    refresh: 'Cari lagi',
    close: 'Tutup',
    button: 'Pilih printer',
  },
  kitchen: {
    unrouted: '{{codes}} belum punya rute dapur. Setel di portal → Routing.',
  },
  refund: {
    title: 'Kembalikan uang',
    remaining: 'sisa {{n}}',
    note: 'Keterangan',
    estimate: 'Perkiraan:',
    submit: 'Kembalikan',
    cancel: 'Batal',
    button: 'Refund',
    done: 'Dikembalikan {{amount}}',
  },
  sales: {
    title: 'Penjualan hari ini',
    bills: 'Bill',
    guests: 'Tamu',
    total: 'Total',
    empty: 'Belum ada penjualan hari ini.',
    close: 'Tutup',
    button: 'Penjualan hari ini',
  },
  unpair: {
    button: 'Lepas perangkat ini',
    title: 'Lepas perangkat',
    what: 'Perangkat ini berhenti terdaftar. Ia harus dipasangkan lagi dengan kode dari portal — bisa ke outlet atau peran yang lain.',
    pending: '{{n}} penjualan belum diakui portal. Kirim dulu.',
    staffCode: 'Kode staf administrator',
    pin: 'PIN',
    needsPermission: 'Staf ini tak punya izin mengubah setelan.',
    confirm: 'Lepas',
    cancel: 'Batal',
  },
  soldOut: {
    title: 'Stok habis',
    /* ⚠️ Pendek: ia muncul DI DALAM kartu menu yang sempit. */
    badge: 'HABIS',
    blocked: '{{name}} sedang habis.',
    mark: 'Tandai habis',
    restock: 'Ada lagi',
    cancel: 'Batal',
    reminder: '{{count}} item ditandai habis',
    since: '{{days}} hari',
    none: 'Tak ada item yang ditandai habis.',
    keep: 'Tutup',
  },
  /**
   * ⭐ Penanda pesanan (meja / antrean / nama) + jumlah tamu — tampil di ATAS bill, dan di KDS.
   * ⛔ Nama boleh berupa ANGKA (Imron) — karena itu jenisnya wajib disebut, bukan disiratkan.
   */
  tag: {
    kind: { table: 'MEJA', queue: 'ANTREAN', name: 'NAMA' },
    pax: '{{n}} org',
    paxLabel: 'Jumlah orang',
    empty: 'Belum ada penanda — ketuk untuk isi meja / antrean / nama',
  },
  parked: {
    button: 'Tahan',
    title: 'Tertahan ({{n}})',
    close: 'Tutup',
    empty: 'Belum ada pesanan yang tertahan.',
    tag: 'Tanda',
    items: 'Item',
    total: 'Total',
    age: 'Sejak',
    resume: 'Panggil',
    justNow: 'baru',
    minutes: '{{n}} mnt',
    heldBy: 'dibuka {{name}}',
    kind: { table: 'meja', name: 'nama', queue: 'antrean' },
    ask: 'Tahan pesanan ini',
    tagName: 'Nama tamu',
    tagQueue: 'Nomor antrean',
    tagTable: 'Meja yang sedang dilayani',
    noTable: 'Pesanan ini belum menempel pada meja.',
  },
  title: 'Kasir',
  backToTables: '← Meja',
  drawer: {
    button: 'Buka Laci',
    title: 'Buka Laci Kas',
    /** ⚠️ Kalimatnya menyebut ALASAN kenapa PIN diminta lagi — bukan sekadar "masukkan PIN". */
    pinAgain: 'Masukkan PIN Anda lagi untuk membuka laci',
    reason: 'Alasan',
    note: 'Keterangan',
    open: 'Buka',
    cancel: 'Batal',
    ok: 'Laci dibuka',
    reviewed: 'Sudah beberapa kali membuka laci sesi ini — dicatat untuk ditinjau penyelia',
    needsSession: 'Buka sesi kas dulu sebelum membuka laci',
  },
  /** ⭐⭐ Paksa-buka — jalan keluar saat bill terkunci di perangkat yang sudah mati. */
  takeOver: {
    title: 'Bill ini sedang dipegang',
    /** ⚠️ Nama + lama, karena itu satu-satunya bahan kasir menilai "mati atau memang sedang kerja". */
    heldBy: 'Dipegang {{name}} sejak {{duration}} lalu',
    lessThanMinute: 'kurang dari semenit',
    minutes: '{{n}} menit',
    /** ⛔ Anjuran menunggu dibaca lebih dulu daripada tombol merebut. */
    waitFirst:
      'Kalau orangnya memang sedang bekerja, tunggu — dua orang menyunting satu bill menghasilkan ' +
      'dua layar berbeda, dan yang menutup belakangan menimpa pekerjaan yang satunya. Paksa-buka ' +
      'hanya untuk perangkat yang mati.',
    reason: 'Alasan',
    note: 'Keterangan',
    reviewed: 'Paksa-buka selalu dicatat dan ditandai untuk ditinjau penyelia.',
    confirm: 'Paksa buka',
    cancel: 'Tunggu saja',
  },
  /** ⭐⭐ Buka kembali bill LUNAS — kesalahan catat yang belum sempat jadi kenyataan. */
  reopen: {
    button: 'Buka kembali',
    title: 'Buka kembali bill lunas',
    /*
     * ⚠️ Menyebut BATASNYA di depan. Kasir yang menekan lalu ditolak akan mengira sistemnya rusak;
     * yang membaca batasnya lebih dulu tahu kapan jawabannya memang refund.
     */
    warning:
      'Hanya untuk salah catat yang tamunya masih di depan Anda. Seluruh tender dibalik (ditandai, ' +
      'tidak dihapus) dan bill kembali ke status tertagih. Sesudah sesi kas ditutup atau transaksi ' +
      'dibukukan, jawabannya refund — bukan ini.',
    reason: 'Alasan',
    note: 'Keterangan',
    confirm: 'Buka kembali',
    cancel: 'Batal',
    ok: '{{n}} tender dibalik — bill kembali bisa disunting',
  },
  newOrder: 'Pesanan Baru',
  addItem: 'Tambah item',
  businessDate: 'Tanggal usaha',
  /* ⚠️ Chip nama staf adalah SATU-SATUNYA jalan ke sesi kas & tutup toko — dan nama orang tak
     memberi tahu itu. Labelnya yang mengatakan, bukan kasir yang harus menebak. */
  sessionChip: 'Sesi kas',
  /* ⚠️ Menyebut UMUR salinannya, bukan cuma "luring" — salinan sejam lalu ≠ salinan minggu lalu. */
  catalogCached: '⚠ Menu salinan {{at}} — server tak terjangkau, harga bisa sudah berubah',
  /* ⭐ Label persis "SYNC" (Imron, 2026-08-06) — istilah yang sudah dipakai timnya sehari-hari. */
  /**
   * ⭐ Batas jualan luring. ⚠️ Pesannya menyebut **apa yang harus dilakukan** — "hubungkan ke
   * internet" bisa ditindaklanjuti, "batas terlampaui" tidak.
   */
  offlineLimit: {
    badge: '⚠ Luring {{days}} hari (batas {{max}})',
    blocked: 'Sudah {{days}} hari tanpa server — bill baru ditahan. Hubungkan ke internet dulu; bill yang sedang terbuka tetap bisa diselesaikan.',
  },
  sync: {
    button: '● SYNC',
    busy: 'SYNC…',
    /* ⚠️ Menyebut bahwa bill yang terbuka AMAN — tanpa itu kasir menunda SYNC sampai sepi, dan
       "sampai sepi" di restoran ramai berarti tak pernah. */
    hint: 'Ada data baru dari kantor. Bill yang sedang terbuka tidak berubah.',
    /* ⭐ Angka menit dibiarkan apa adanya — "tertinggal 190 mnt" tetap terbaca, dan membaginya jadi
       jam/menit menambah dua kunci demi ketelitian yang tak mengubah tindakan siapa pun. */
    behind: '{{n}} mnt',
    askTitle: 'Ada data baru dari kantor',
    askBody: 'Menunggu {{n}} menit. SYNC sekarang selagi tak ada bill?',
    now: 'SYNC sekarang',
    later: 'Nanti',
  },
  /* ⚠️ Sesi dari salinan ≠ sesi biasa: izinnya berumur dan tak tahu pencabutan yang terjadi. */
  offlineSession: '⚠ Sesi luring — izin dari salinan lokal',
  department: 'Departemen',
  category: 'Kategori',
  all: 'Semua',
  other: 'Lainnya',
  noMatch: 'Tak ada yang cocok di saringan ini',
  itemCount: '{{count}} item',
  searchMenu: 'Cari menu…',
  table: 'Meja',
  guest: 'Tamu',
  summary: {
    subtotal: 'Subtotal',
    /* ⚠️ Menandai angka yang belum jadi tagihan resmi — lihat komentar di `CashierShell`. */
    estimate: '(perkiraan)',
    discount: 'Diskon',
    serviceCharge: 'Service Charge',
    pb1: 'PB1',
    total: 'Total',
  },
  action: {
    /** ⚠️ Pendek dengan sengaja: tombol yang labelnya melipat dua baris menaikkan tinggi seluruh
        panel — dan yang terdorong keluar layar justru tombol di bawahnya. */
    sendToKitchen: 'Kirim',
    bill: 'Hitung Bill',
    billNeedsSend: 'Kirim pesanan ke dapur dulu',
    pay: 'Bayar',
    openPay: 'Buka Pembayaran',
    cancelBill: 'Batal',
    void: 'Void',
  },
  pay: {
    title: 'Pembayaran',
    short: 'Kurang {{amount}}',
    settled: 'Lunas',
    /* ⚠️ Menyebut IZIN, bukan "tak boleh" — supaya yang membacanya tahu harus minta apa. */
    noPermission: 'Anda tak punya izin menerima pembayaran. Panggil kasir untuk menyelesaikan bill ini.',
    discountApplied: '🎫 Potongan terpasang {{amount}}',
    chooseMethod: 'Pilih metode bayar',
    referenceNo: 'Nomor acuan',
    split: 'Bagi bayar',
    splitShort: 'Kurang {{amount}}',
    splitChange: 'Kembalian {{amount}}',
    splitExact: 'Pas',
    splitNonCashOver: 'Metode non-tunai tak boleh melebihi sisa — kembalian keluar dari laci.',
    approvalCode: 'Kode approval (slip EDC)',
    approvalRequired: 'Kode approval wajib untuk kartu.',
    cardLast4: '4 digit terakhir',
    cardLast4Bad: '4 digit, atau kosongkan.',
    referenceRequired: 'Metode ini wajib nomor acuan',
    /* ⭐ Dibaca kasir SEBELUM ia mengambil uang dari laci — bukan sesudah. */
    change: 'Kembali {{amount}}',
  },
  sheet: { close: 'Tutup panel' },
  print: {
    bill: 'Cetak Tagihan',
    receipt: 'Cetak Struk',
    sent: '✓ Terkirim ke printer',
    copyNo: 'cetakan ke-{{n}}',
    /* ⚠️ Dibuka dengan "tercatat" — kasir perlu tahu menekan lagi tak menolong. */
    recordedNotPrinted: '⚠ Tercatat, tapi kertas belum keluar:',
    noJob: 'perangkat ini belum punya profil printer — minta manajer menyetelnya di portal',
  },
  line: {
    title: 'Baris terpilih',
    increment: 'Tambah jumlah',
    decrement: 'Kurangi jumlah',
    remove: 'Hapus',
    sent: '✓ terkirim',
    sentPartial: '✓ {{sent}} terkirim · {{fresh}} baru',
    /** ⚠️ Menyebut JALAN KELUARNYA — dan menyebut kenapa jalannya semahal itu. */
    sentLocked: 'Sudah terkirim ke dapur. Membatalkannya lewat ☰ dan berlasan.',
  },
  admin: {
    title: 'Administrasi',
    void: 'Batalkan Bill',
    voidConfirm: 'Batalkan',
    voidWarning: 'Bill ini dibatalkan seluruhnya dan tercatat atas nama Anda.',
    voidNeedsBill: 'Belum ada bill yang bisa dibatalkan',
    reload: 'Muat ulang aplikasi',
    logout: 'Keluar (ganti staf)',
    /** ⚠️ Menyebut APA yang hilang, bukan sekadar "yakin?". */
    logoutWarning: 'Ada pesanan yang belum dikirim. Kalau keluar sekarang, isinya hilang. Lanjut?',
    settings: 'Setelan',
    outlet: 'Outlet',
    device: 'Perangkat',
  },
  emptyOrder: 'Belum ada item. Pilih menu di sebelah kiri.',
  itemAdded: '{{name}} ditambahkan',
  menuEmpty: 'Katalog kosong — outlet ini belum punya menu berharga',
  loadFailed: 'Gagal memuat katalog',
  exactCash: 'Uang pas',
  modifier: {
    title: 'Pilihan',
    required: 'wajib',
    freeText: 'Catatan (mis. sausnya dipisah)',
    add: 'Tambah',
    cancel: 'Batal',
    stillRequired: '"{{group}}" belum dipilih',
  },
  /**
   * Ganti PIN sendiri (Imron, 2026-08-07) — ⛔ PIN lama WAJIB.
   * ⚠️ "PIN" tetap Inggris (T3): itu istilah yang dipakai di lantai, bukan terjemahan.
   */
  pin: {
    button: 'Ubah PIN',
    title: 'Ubah PIN saya',
    hint:
      'PIN lama diminta supaya terminal yang ditinggal terbuka tak bisa dipakai memindahkan PIN ' +
      'orang lain. 4–8 digit angka.',
    current: 'PIN sekarang',
    next: 'PIN baru',
    repeat: 'Ketik ulang PIN baru',
    mismatch: 'Ketikan ulangnya belum sama',
    save: 'Simpan',
    cancel: 'Batal',
    ok: 'PIN diganti — pakai yang baru mulai login berikutnya',
  },
  /**
   * Set menu — stepper di dalam container menu (bukan dialog melayang).
   * ⚠️ "Set menu" tetap Inggris (T3): itu istilah yang dipakai di lantai, bukan terjemahan.
   */
  combo: {
    step: 'Langkah {{n}} dari {{total}}',
    steps: 'Langkah',
    pickExactly: 'pilih {{n}}',
    pickRange: 'pilih {{min}}–{{max}}',
    back: '← Kembali',
    next: 'Lanjut →',
    add: 'Tambah ke bill',
    cancel: 'Batal',
    stillRequired: '"{{group}}" belum dipilih ({{n}} lagi)',
    /* ⚠️ Menyebut MASTERNYA yang kurang — kalau tidak, kasir mengira layarnya rusak. */
    noGroup: 'Set menu ini belum punya pilihan apa pun. Susun komponennya dulu di portal.',
  },
  promo: {
    button: 'Promo',
    title: 'Terapkan promo',
    code: 'Kode promo',
    apply: 'Terapkan',
    cancel: 'Batal',
    applied: 'Promo {{code}} terpasang · potongan {{amount}}',
    /* ⚠️ Kalimat luring: menyebut KODE-nya, dan tak menjanjikan angka yang belum dihitung server. */
    appliedOffline: 'Promo {{code}} terpasang',
    unknown: 'Promo {{code}} tak ada di katalog perangkat ini. Tekan SYNC dulu.',
    needsServer: 'Promo {{code}} butuh persetujuan penyelia — tak bisa dipakai di mode satu-tablet.',
    belowMin: 'Belanja belum mencapai minimum promo {{code}}.',
    invalid: {
      not_started: 'Promo {{code}} belum berlaku.',
      expired: 'Promo {{code}} sudah lewat masa berlakunya.',
      wrong_day: 'Promo {{code}} tak berlaku hari ini.',
      wrong_time: 'Promo {{code}} tak berlaku pada jam ini.',
      below_min_subtotal: 'Belanja belum mencapai minimum promo {{code}}.',
    },

    /* ⚠️ Menyebut ANGKA-nya: penyelia yang menekan PIN tanpa tahu berapa rupiah yang disetujui
       hanya menandatangani kertas kosong. */
    needsApproval: 'Promo ini butuh persetujuan penyelia · potongan {{amount}}',
    approverCode: 'Kode penyelia',
    approveApply: 'Setujui & terapkan',
  },
};
export const cashierEn: typeof cashierId = {
  clearCache: {
    button: 'Clear tablet copies',
    what: 'Removes the menu, cash-session, and business-date copies stored on this tablet. All are fetched again from the server on reload.',
    keeps: 'NOT removed: device pairing (no re-pairing needed) and the sales queue.',
    pending: '{{n}} sale(s) still waiting for portal acknowledgement. Send them first — clearing now deletes money already taken from guests.',
    offline: 'Server unreachable. Clearing now discards the only copy of the menu exactly when it cannot be fetched again.',
    staffCode: 'Administrator staff code',
    pin: 'PIN',
    needsPermission: 'This staff has no permission to change settings (`master:edit`).',
    confirm: 'Clear',
  },
  find: {
    title: 'Find bill',
    hint: 'Table, queue, name, or bill no.',
    search: 'Search',
    empty: 'No matching bill.',
    truncated: 'Results truncated. Narrow your search.',
    close: 'Close',
    button: 'Find bill',
    range: {
      0: 'Today',
      1: 'Yesterday',
      7: '7 days',
    },
  },
  printerSetup: {
    copy: 'Copy MAC',
    copied: '✓ copied',
    title: 'Choose printer',
    use: 'Use',
    done: '{{name}} is now used by this terminal.',
    empty: 'No printer visible. Make sure the print agent is installed and running, and the printer is paired in Settings → Bluetooth.',
    lanHint: 'Network (LAN) and USB printers are set in the portal: Floor → Printer column.',
    refresh: 'Search again',
    close: 'Close',
    button: 'Choose printer',
  },
  kitchen: {
    unrouted: '{{codes}} has no kitchen route. Set it in portal → Routing.',
  },
  refund: {
    title: 'Refund',
    remaining: '{{n}} left',
    note: 'Note',
    estimate: 'Estimate:',
    submit: 'Refund',
    cancel: 'Cancel',
    button: 'Refund',
    done: 'Refunded {{amount}}',
  },
  sales: {
    title: "Today's sales",
    bills: 'Bills',
    guests: 'Guests',
    total: 'Total',
    empty: 'No sales yet today.',
    close: 'Close',
    button: "Today's sales",
  },
  unpair: {
    button: 'Unpair this device',
    title: 'Unpair device',
    what: 'This device stops being registered. It must be paired again with a code from the portal — possibly to a different outlet or role.',
    pending: '{{n}} sales not yet acknowledged. Send them first.',
    staffCode: 'Administrator staff code',
    pin: 'PIN',
    needsPermission: 'This staff cannot change settings.',
    confirm: 'Unpair',
    cancel: 'Cancel',
  },
  soldOut: {
    title: 'Sold out',
    badge: 'SOLD OUT',
    blocked: '{{name}} is sold out.',
    mark: 'Mark sold out',
    restock: 'Back in stock',
    cancel: 'Cancel',
    reminder: '{{count}} items marked sold out',
    since: '{{days}} d',
    none: 'Nothing is marked sold out.',
    keep: 'Close',
  },
  tag: {
    kind: { table: 'TABLE', queue: 'QUEUE', name: 'NAME' },
    pax: '{{n}} pax',
    paxLabel: 'Number of guests',
    empty: 'No tag yet — tap to set table / queue / name',
  },
  parked: {
    button: 'Hold',
    title: 'On hold ({{n}})',
    close: 'Close',
    empty: 'Nothing on hold yet.',
    tag: 'Tag',
    items: 'Items',
    total: 'Total',
    age: 'Since',
    resume: 'Resume',
    justNow: 'just now',
    minutes: '{{n}} min',
    heldBy: 'open by {{name}}',
    kind: { table: 'table', name: 'name', queue: 'queue' },
    ask: 'Hold this order',
    tagName: 'Guest name',
    tagQueue: 'Queue number',
    tagTable: 'The table being served',
    noTable: 'This order is not attached to a table.',
  },
  title: 'Cashier',
  backToTables: '← Tables',
  drawer: {
    button: 'Open Drawer',
    title: 'Open Cash Drawer',
    pinAgain: 'Re-enter your PIN to open the drawer',
    reason: 'Reason',
    note: 'Note',
    open: 'Open',
    cancel: 'Cancel',
    ok: 'Drawer opened',
    reviewed: 'Drawer opened several times this session — logged for supervisor review',
    needsSession: 'Open a cash session before opening the drawer',
  },
  takeOver: {
    title: 'This bill is being held',
    heldBy: 'Held by {{name}} since {{duration}} ago',
    lessThanMinute: 'less than a minute',
    minutes: '{{n}} minutes',
    waitFirst:
      'If they are actually working on it, wait — two people editing one bill produce two ' +
      'different screens, and whoever closes last overwrites the other. Force-open is for ' +
      'devices that died.',
    reason: 'Reason',
    note: 'Note',
    reviewed: 'Force-open is always logged and flagged for supervisor review.',
    confirm: 'Force open',
    cancel: 'Wait instead',
  },
  reopen: {
    button: 'Reopen',
    title: 'Reopen a settled bill',
    warning:
      'Only for a mis-entry while the guest is still in front of you. Every tender is reversed ' +
      '(flagged, not deleted) and the bill returns to billed. Once the cash session is closed or ' +
      'the sale is posted, the answer is a refund — not this.',
    reason: 'Reason',
    note: 'Note',
    confirm: 'Reopen',
    cancel: 'Cancel',
    ok: '{{n}} tenders reversed — the bill is editable again',
  },
  newOrder: 'New Order',
  addItem: 'Add item',
  businessDate: 'Business date',
  sessionChip: 'Cash session',
  catalogCached: '⚠ Menu copy from {{at}} — server unreachable, prices may have changed',
  offlineLimit: {
    badge: '⚠ Offline {{days}}d (limit {{max}})',
    blocked: '{{days}} days without the server — new bills are on hold. Reconnect first; the open bill can still be completed.',
  },
  sync: {
    button: '● SYNC',
    busy: 'SYNC…',
    hint: 'New data from head office. Open bills are not affected.',
    behind: '{{n}} min',
    askTitle: 'New data from head office',
    askBody: 'Waiting {{n}} minutes. SYNC now while no bill is open?',
    now: 'SYNC now',
    later: 'Later',
  },
  offlineSession: '⚠ Offline session — permissions from a local copy',
  department: 'Department',
  category: 'Category',
  all: 'All',
  other: 'Others',
  noMatch: 'Nothing matches this filter',
  itemCount: '{{count}} items',
  searchMenu: 'Search menu…',
  table: 'Table',
  guest: 'Guests',
  summary: {
    subtotal: 'Subtotal',
    estimate: '(estimate)',
    discount: 'Discount',
    // T1: service charge & PB1 dipertahankan — muncul di struk & laporan pajak.
    serviceCharge: 'Service Charge',
    pb1: 'PB1',
    total: 'Total',
  },
  action: {
    sendToKitchen: 'Send',
    bill: 'Calculate Bill',
    billNeedsSend: 'Send the order to the kitchen first',
    pay: 'Pay',
    openPay: 'Open payment',
    cancelBill: 'Cancel',
    void: 'Void', // T3
  },
  pay: {
    title: 'Payment',
    short: 'Short by {{amount}}',
    settled: 'Settled',
    noPermission: 'You are not allowed to take payment. Ask a cashier to settle this bill.',
    discountApplied: '🎫 Discount applied {{amount}}',
    chooseMethod: 'Choose payment method',
    referenceNo: 'Reference no.',
    split: 'Split payment',
    splitShort: 'Short {{amount}}',
    splitChange: 'Change {{amount}}',
    splitExact: 'Exact',
    splitNonCashOver: 'Non-cash cannot exceed the remainder — change comes from the drawer.',
    approvalCode: 'Approval code (EDC slip)',
    approvalRequired: 'Approval code required for cards.',
    cardLast4: 'Last 4 digits',
    cardLast4Bad: '4 digits, or leave blank.',
    referenceRequired: 'This method requires a reference number',
    change: 'Change {{amount}}',
  },
  sheet: { close: 'Close panel' },
  print: {
    bill: 'Print Bill',
    receipt: 'Print Receipt',
    sent: '✓ Sent to printer',
    copyNo: 'copy #{{n}}',
    recordedNotPrinted: '⚠ Recorded, but no paper came out:',
    noJob: 'this device has no printer profile yet — ask a manager to set it in the portal',
  },
  line: {
    title: 'Selected line',
    increment: 'Increase quantity',
    decrement: 'Decrease quantity',
    remove: 'Remove',
    sent: '✓ sent',
    sentPartial: '✓ {{sent}} sent · {{fresh}} new',
    sentLocked: 'Already sent to the kitchen. Voiding goes through ☰, with a reason.',
  },
  admin: {
    title: 'Administration',
    void: 'Void Bill', // T3
    voidConfirm: 'Void',
    voidWarning: 'This bill is voided in full and recorded under your name.',
    voidNeedsBill: 'No bill to void yet',
    reload: 'Reload app',
    logout: 'Log out (switch staff)',
    logoutWarning: 'There are unsent items. Leaving now discards them. Continue?',
    settings: 'Settings',
    outlet: 'Outlet',
    device: 'Device',
  },
  emptyOrder: 'No items yet. Pick from the menu on the left.',
  itemAdded: '{{name}} added',
  menuEmpty: 'Catalog is empty — this outlet has no priced menu yet',
  loadFailed: 'Failed to load catalog',
  exactCash: 'Exact amount',
  modifier: {
    title: 'Options',
    required: 'required',
    freeText: 'Note (e.g. sauce on the side)',
    add: 'Add',
    cancel: 'Cancel',
    stillRequired: '"{{group}}" not chosen yet',
  },
  pin: {
    button: 'Change PIN',
    title: 'Change my PIN',
    hint:
      'The current PIN is required so a terminal left unattended cannot be used to move somebody ' +
      "else's PIN. 4–8 digits.",
    current: 'Current PIN',
    next: 'New PIN',
    repeat: 'Repeat new PIN',
    mismatch: 'The repeat does not match yet',
    save: 'Save',
    cancel: 'Cancel',
    ok: 'PIN changed — use the new one from your next sign-in',
  },
  combo: {
    step: 'Step {{n}} of {{total}}',
    steps: 'Steps',
    pickExactly: 'pick {{n}}',
    pickRange: 'pick {{min}}–{{max}}',
    back: '← Back',
    next: 'Next →',
    add: 'Add to bill',
    cancel: 'Cancel',
    stillRequired: '"{{group}}" not chosen yet ({{n}} to go)',
    noGroup: 'This set menu has no options yet. Compose its components in the portal first.',
  },
  promo: {
    button: 'Promo',
    title: 'Apply a promotion',
    code: 'Promo code',
    apply: 'Apply',
    cancel: 'Cancel',
    applied: 'Promo {{code}} applied · discount {{amount}}',
    appliedOffline: 'Promo {{code}} applied',
    unknown: 'Promo {{code}} is not in this device catalog. Press SYNC first.',
    needsServer: 'Promo {{code}} needs supervisor approval — not available in single-tablet mode.',
    belowMin: 'Spend has not reached the minimum for promo {{code}}.',
    invalid: {
      not_started: 'Promo {{code}} is not active yet.',
      expired: 'Promo {{code}} has expired.',
      wrong_day: 'Promo {{code}} does not apply today.',
      wrong_time: 'Promo {{code}} does not apply at this hour.',
      below_min_subtotal: 'Spend has not reached the minimum for promo {{code}}.',
    },
    needsApproval: 'This promotion needs supervisor approval · discount {{amount}}',
    approverCode: 'Supervisor code',
    approveApply: 'Approve & apply',
  },
};
